import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  AGING_BUCKET_LABELS,
  CHART_FILTER_KEYS,
  CYCLE_BUCKET_LABELS,
  DEFAULT_RANGE_DAYS,
  defaultRange,
  lastDays,
  RANGE_PRESETS,
  formatDate,
  isSearchOnlyTab,
  tabSection,
  TAB_LABEL,
  PO_STAGES,
  REQ_STAGES,
  REQ_TYPE_LABEL,
  REGIONS,
  REQ_TYPE_VALUES,
  type FilterKey,
  type Search,
} from "@po/shared";
import { api, type Options } from "../lib/api";
import { stageLabel } from "../lib/format";
import { presetDefaults, useDashboard } from "../pages/Dashboard";
import { DateRangePicker } from "./DateRangePicker";
import { MultiSelect } from "./MultiSelect";
import { TopBar } from "./Sidebar";
import { TabBar } from "./TabBar";

const FLAG_LABEL = { open_for_changes: "Open for changes", anomaly: "Anomaly", critical: "Critical item", over_budget: "Over budget" } as const;

export function todayWib(): string {
  return new Date(Date.now() + 7 * 3600_000).toISOString().slice(0, 10);
}

function chipLabel(key: FilterKey, v: string | number, agingEdges: number[]): string {
  switch (key) {
    case "stage":
      return `Stage: ${stageLabel(String(v))}`;
    case "poStage":
      return `PO stage: ${stageLabel(String(v), "po")}`;
    case "age":
      return `Age: ${AGING_BUCKET_LABELS(agingEdges)[Number(v)]}`;
    case "cycle":
      return `Cycle: ${CYCLE_BUCKET_LABELS[Number(v)]}`;
    case "purchaseType":
      return `Purchase type: ${v}`;
    case "account":
      return `Account: ${v}`;
    default:
      return String(v);
  }
}

/** A header button that opens a panel below it; Escape or a click outside closes it. */
function Popover({ label, count = 0, width = "w-[min(560px,90vw)]", children }: { label: string; count?: number; width?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} className={`ghost ${count ? "text-brand" : ""}`}>
        {label}
        {count > 0 && <span className="tnum rounded-badge bg-brand-2 text-white text-[11px] px-1.5">{count}</span>}
        <span aria-hidden className="text-muted text-[10px]">▼</span>
      </button>
      {open && <div className={`card absolute left-0 z-30 mt-1 ${width} shadow-lg p-3`}>{children}</div>}
    </div>
  );
}

/** "Last 180 days" for the default range, "Last 30 days" for a quick range, else the two dates. */
function rangeLabel(from: string | undefined, to: string | undefined, today: string): string {
  if (!from && !to) return `Last ${DEFAULT_RANGE_DAYS} days`;
  const quick = RANGE_PRESETS.find((n) => {
    const r = lastDays(today, n);
    return r.from === from && r.to === to;
  });
  if (quick) return `Last ${quick} days`;
  const r = defaultRange(today);
  return `${formatDate(from ?? r.from)} – ${formatDate(to ?? r.to)}`;
}

/** Page title, the header filters, the tabs with search, and the chart-filter chips (SPEC §6). */
export function PageHeader() {
  const { search, filters, setSearch, preset, me, tab } = useDashboard();
  const opts = useQuery({ queryKey: ["options"], queryFn: () => api<Options>("/options"), staleTime: 10 * 60_000 });
  const [q, setQ] = useState(search.q ?? "");
  useEffect(() => setQ(search.q ?? ""), [search.q]);
  // Search is applied after a short pause, so typing does not reload every keystroke.
  useEffect(() => {
    const t = setTimeout(() => q !== (search.q ?? "") && setSearch({ q: q || undefined }), 400);
    return () => clearTimeout(t);
  }, [q]);

  const budgets = tab === "budgets";
  // The address book and Item Info are not about requisitions or POs: of the filters only the
  // search applies, and on the item tree not even that.
  // A section of several tabs (Item, Unit and User Info) shows its own tab bar on narrow screens.
  const section = tabSection(tab);
  const addresses = isSearchOnlyTab(tab);
  const today = todayWib();
  const range = defaultRange(today);
  const set = (k: FilterKey) => (v: string[]) => setSearch({ [k]: v.length ? v : undefined } as Partial<Search>);
  const o = opts.data;
  const deptOptions = (o?.departments ?? []).map((d) => ({ value: d, group: d.startsWith("V-") ? "Vessel" : d.startsWith("O-") ? "Office" : "Other" }));
  // Projects narrow to the selected vessels; a project already chosen stays listed so it can be removed.
  const projectOptions = (o?.projects ?? [])
    .filter((p) => !filters.vessel?.length || p.vessels.some((v) => filters.vessel!.includes(v)) || filters.project?.includes(p.no))
    .map((p) => ({ value: p.no, label: p.name ? `${p.no} · ${p.name}` : p.no }));
  // What "More filters" holds, counted on its button.
  const more = (["type", "dept", "stage", "poStage", "flags", "urgent", "supplier", "region", "approver", "project"] as FilterKey[]).filter((k) => {
    const v = filters[k];
    return Array.isArray(v) ? v.length > 0 : v !== undefined;
  }).length;

  const clear = () => {
    const cleared = Object.fromEntries(
      ["vessel", "type", "dept", "stage", "poStage", "flags", "urgent", "supplier", "region", "showDoNotUse", "approver", "project", "from", "to", "q", "purchaseType", "account", "age", "cycle"].map((k) => [k, undefined]),
    );
    setSearch({ ...cleared, ...presetDefaults(preset, me) });
    setQ("");
  };

  const chips = CHART_FILTER_KEYS.flatMap((k) => ((filters[k] as (string | number)[] | undefined) ?? []).map((v) => ({ k, v })));
  const vessels = filters.vessel ?? [];

  const vesselPicker = (className: string) => (
    <MultiSelect
      label={vessels.length === 1 ? vessels[0]! : vessels.length ? "Vessels" : "All vessels"}
      name="vessels"
      className={className}
      align="right"
      options={(o?.vessels ?? []).map((v) => ({ value: v }))}
      value={vessels}
      onChange={set("vessel")}
      searchable
    />
  );

  return (
    <>
    {/* The vessel sits top right, where Sertica keeps its unit picker. */}
    <TopBar>{!addresses && vesselPicker("ghost font-semibold")}</TopBar>
    <section aria-label="Filters" className="space-y-2 px-4 md:px-5 pt-3">
      <h1 className="lg:hidden text-[15px] font-bold uppercase tracking-[0.02em]">{budgets ? "Budgeting" : section ? section.label : tab === "addresses" ? "Address Book" : addresses ? TAB_LABEL[tab] : "Procurement"}</h1>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
        {!budgets && tab !== "item-tree" && tab !== "certificate-browser" && (
          <label className="relative w-full md:w-72">
            <span className="sr-only">Search requisition or PO number</span>
            <svg aria-hidden width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink2">
              <circle cx="11" cy="11" r="6.5" />
              <path d="M16 16l4.5 4.5" />
            </svg>
            <input className="search w-full" type="search" placeholder="Search" title="Search number, vessel, item, description" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
        )}
        {!addresses && <div className="lg:hidden">{vesselPicker("ghost")}</div>}
        {!budgets && !addresses && (
          <>
            <Popover label={rangeLabel(filters.from, filters.to, today)} width="w-[min(680px,94vw)]">
              <DateRangePicker
                value={{ from: filters.from ?? range.from, to: filters.to ?? range.to }}
                today={today}
                onChange={(r) => setSearch({ from: r?.from, to: r?.to })}
              />
            </Popover>
            <Popover label="Add filters" count={more}>
              <div className="flex flex-wrap items-center gap-2">
                <MultiSelect label="Type" options={REQ_TYPE_VALUES.map((t) => ({ value: t, label: REQ_TYPE_LABEL[t] }))} value={filters.type ?? []} onChange={set("type")} />
                <MultiSelect label="Department" options={deptOptions} value={filters.dept ?? []} onChange={set("dept")} searchable />
                <MultiSelect label="Stage" options={REQ_STAGES.map((st) => ({ value: st, label: stageLabel(st) }))} value={filters.stage ?? []} onChange={set("stage")} />
                <MultiSelect label="PO stage" options={PO_STAGES.map((st) => ({ value: st, label: stageLabel(st, "po") }))} value={filters.poStage ?? []} onChange={set("poStage")} />
                <MultiSelect
                  label="Flags"
                  // Over budget only with budget access (SPEC §6.3).
                  options={Object.entries(FLAG_LABEL)
                    .filter(([value]) => value !== "over_budget" || me.canSeeBudgets)
                    .map(([value, label]) => ({ value, label }))}
                  value={filters.flags ?? []}
                  onChange={set("flags")}
                />
                <label className="flex items-center gap-1.5 text-[13px]">
                  <span className="sr-only">Urgent</span>
                  <select className="input" value={filters.urgent ?? ""} onChange={(e) => setSearch({ urgent: (e.target.value || undefined) as Search["urgent"] })}>
                    <option value="">Urgent: any</option>
                    <option value="yes">Urgent only</option>
                    <option value="no">Not urgent</option>
                  </select>
                </label>
                <MultiSelect label="Supplier" options={(o?.suppliers ?? []).map((v) => ({ value: v }))} value={filters.supplier ?? []} onChange={set("supplier")} searchable />
                <MultiSelect label="Supplier region" options={REGIONS.map((v) => ({ value: v }))} value={filters.region ?? []} onChange={set("region")} />
                <MultiSelect label="Approver" options={(o?.approvers ?? []).map((v) => ({ value: v }))} value={filters.approver ?? []} onChange={set("approver")} searchable />
                <MultiSelect label="Project" options={projectOptions} value={filters.project ?? []} onChange={set("project")} searchable />
              </div>
              <button onClick={clear} className="text-[13px] text-brand font-semibold mt-3">
                Clear all filters
              </button>
            </Popover>
          </>
        )}
      </div>
      {budgets
        ? me.canSeeBudgets && <p className="text-[11.5px] text-muted">On Budgets only the Vessel filter applies. Budgets follow the current budget period, not the date range.</p>
        : (!addresses || section) && (
          // Tabs live in the side rail from 1024 px; below that they sit here.
          <div className="lg:hidden border-b border-line">
            <TabBar tabs={section?.tabs} />
          </div>
        )}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5" aria-label="Chart filters">
          {chips.map(({ k, v }) => (
            <button
              key={`${k}:${v}`}
              onClick={() => {
                const cur = (filters[k] as (string | number)[]).filter((x) => x !== v);
                setSearch({ [k]: cur.length ? cur : undefined } as Partial<Search>);
              }}
              className="inline-flex items-center gap-1 rounded-badge bg-card border border-line text-[12px] px-2 py-0.5"
              aria-label={`Remove ${chipLabel(k, v, me.settings.agingEdges)}`}
            >
              {chipLabel(k, v, me.settings.agingEdges)} <span aria-hidden>✕</span>
            </button>
          ))}
        </div>
      )}
    </section>
    </>
  );
}
